import React, { Component } from 'react';
import { Button, Col, ControlLabel, FormControl, FormGroup, Grid, Modal, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import moment, { isMoment } from 'moment';
import DateTime from 'react-datetime';

import { DATE_FORMAT } from '../common/dateTimeFormats';
import { isDeviceKeyValid } from '../common/keyValidation';
import OverlayLoader from '../components/overlayLoader';
import * as objectDataActions from '../store/objectDataSlice';
import { objectDataNames, objectDataSelectors } from '../store/objectDataSlice';
import * as offlineLicensesActions from '../store/offlineLicensesSlice';
import { offlineLicensesSelectors } from '../store/offlineLicensesSlice';
import { showModalMessage } from '../store/modalsSlice';

import DeviceKeyInputNormalized from '../components/deviceKeyInputNormalized';
import ExpirationDateEdit from '../components/expirationDateEdit';
import { KeyLicenseTypeNames } from '../common/keyLicenseTypes';
import { unwrapResult } from '@reduxjs/toolkit';

const Mode = {
  Create: '0',
  Edit: '1',
};

const getTitle = (type) => ({
  [Mode.Create]: `Create ${type && type.toLowerCase()} license`,
  [Mode.Edit]: "Edit license",
});

const SubmitButtonCaptions = {
  [Mode.Create]: "Create",
  [Mode.Edit]: "Save"
};

const OfflineLicensePeriodYearsCount = 1;

class OfflineLicenseEditDialog extends Component {
  initState = {
    licenseKeyType: '',
    productLicKeyTypes: [],
    productName: '',
    deviceKey: '',
    generationDate: null,
    supportDayCount: null,
    activationDate: null,
    connectionCount: 0,
    webConnectionCount: null,
    webValidityTerm: null,
    clientId: null,
    dicomConnectionLimitationType: null,
  };

  constructor(props) {
    super(props);
    this.state = this.initState;
  }

  static get Mode() {
    return Mode;
  }

  onShow = () => {
    this.setState((state, props) => {
      return {
        ...state,
        supportDayCount: props.supportDayCount,
        activationDate: props.activationDate,
        connectionCount: props.connectionCount,
        webConnectionCount: props.webConnectionCount,
        webValidityTerm: props.webValidityTerm,
        dicomConnectionLimitationType: props.dicomConnectionLimitationType,
        deviceKey: props.deviceKey ? props.deviceKey : ''
      }
    });

    this.props.fetchDicomConnectionLimitationTypes().then(unwrapResult).catch(() => {
      this.onClose();
      this.props.showModalMessage("Error", this.props.dicomConnectionLimitationTypesState.error.toString());
    });

    this.props.fetchLicKeyTypes()
      .then(unwrapResult)
      .then(() => {
        let offlineLicKeyTypes = this.getOfflineLicKeyTypes();
        if (offlineLicKeyTypes.length > 0) {
          let product = offlineLicKeyTypes[0];
          this.setState({ productLicKeyTypes: product.licenseTypes });
          if (product.licenseTypes.length > 0)
            this.setState({ licenseKeyType: product.licenseTypes[0].code });
          this.setState({ productName: product.productName });
        }
      })
      .catch(() => {
        this.onClose();
        this.props.showModalMessage("Error", this.props.licenseKeyTypesState.error.toString());
      });
  };

  onClose = () => {
    this.setState(this.initState, this.props.onClose);
  };

  getOfflineLicKeyTypes = () => {
    return this.props.licenseKeyTypesState.data.map(licenseKeyType => ({
      ...licenseKeyType,
      licenseTypes: licenseKeyType.licenseTypes.filter(type => !type.online && !type.deprecated)
    })).filter(licenseKeyType => licenseKeyType.licenseTypes.length > 0);
  };

  getDicomConnectionLimitationTypes = () => {
    return this.props.dicomConnectionLimitationTypesState.data;
  };

  onLicKeyTypeChanged = (e) => {
    this.setState({ licenseKeyType: e.target.value });
  };

  onDeviceKeyChanged = (normalizedDeviceKey) => {
    this.setState({ deviceKey: normalizedDeviceKey });
  };

  onProductNameChanged = (e) => {
    this.setState({ productName: e.target.value });
    const product = this.getOfflineLicKeyTypes().find(
      prodLicKeyType => prodLicKeyType.productName === e.target.value
    );
    if (product === undefined) {
      this.setState({ licenseKeyType: "" });
      return;
    }
    this.setState({ productLicKeyTypes: product.licenseTypes });
    if (product.licenseTypes.length > 0)
      this.setState({ licenseKeyType: product.licenseTypes[0].code });
    else
      this.setState({ licenseKeyType: "" });
  };

  onActivationDateChange = (value) => {
    const activationDate = isMoment(value) && value.isValid() ? value.format(DATE_FORMAT) : null;
    this.setState({ activationDate });
  };

  onSupportDayCountChanged = (supportDayCount) => {
    this.setState({ supportDayCount });
  };

  onWebValidityTermChanged = (webValidityTerm) => {
    this.setState({ webValidityTerm });
  };

  onConnectionCountChange = (e) => {
    const value = e.target.value;
    const connectionCount = parseInt(value, 10);
    this.setState({ connectionCount });
  };

  onWebConnectionCountChange = (e) => {
    const value = e.target.value;
    let webConnectionCount;
    const re = /^\d+$/;
    if (value.length === 0 || !re.test(value))
      webConnectionCount = null;
    else
      webConnectionCount = parseInt(value, 10);
    this.setState({ webConnectionCount });
  };

  onDicomConnectionLimitationTypeChange = (e) => {
    const value = e.target.value;
    this.setState({ dicomConnectionLimitationType: value });
  };

  isLoading = () => this.props.licenseKeyTypesState.isFetching
    || this.props.dicomConnectionLimitationTypesState.isFetching;

  getLoaderMessage = () => {
    if (this.props.licenseKeyTypesState.isFetching) {
      return "License key types loading...";
    }
    if (this.props.dicomConnectionLimitationTypesState.isFetching) {
      return "Dicom connection types loading...";
    }
  };

  getConnectionCount = () => this.state.connectionCount || 0;

  getWebConnectionCount = () => this.state.webConnectionCount || 0;

  getDicomConnectionLimitationType = () => this.state.dicomConnectionLimitationType;

  getActivationDate = () => this.state.activationDate;

  getSupportDayCount = () => this.state.supportDayCount;

  getWebValidityTerm = () => this.state.webValidityTerm;

  isSubmitButtonDisabled = () => {
    const isCreateMode = this.props.mode === Mode.Create;
    const isEditMode = this.props.mode === Mode.Edit;

    const isEditingValuesAreEqualWithPrevious = (
      this.state.supportDayCount === this.props.supportDayCount &&
      this.state.connectionCount === this.props.connectionCount &&
      this.state.webConnectionCount === this.props.webConnectionCount &&
      this.state.webValidityTerm === this.props.webValidityTerm &&
      this.state.dicomConnectionLimitationType === this.props.dicomConnectionLimitationType &&
      this.state.deviceKey === this.props.deviceKey
    );

    const isAddingValuesAreEqualNull = (
      this.state.supportDayCount === null ||
      this.state.webConnectionCount === null
    );

    return this.isLoading()
      || this.state.licenseKeyType.length === 0
      || (!this.isDeviceKeyCorrect() && isCreateMode)
      || (this.needShowPacsParamControls() && isCreateMode && isAddingValuesAreEqualNull)
      || (isEditMode && isEditingValuesAreEqualWithPrevious);
  };

  getOnSubmitPromise = () => {
    switch (this.props.mode) {
      case Mode.Create:
        return this.getCreatePromise();
      case Mode.Edit:
        return this.getUpdatePromise();
      default:
        return null;
    }
  };

  getCreatePromise = () => {
    const { productName, licenseKeyType, deviceKey, dicomConnectionLimitationType } = this.state;
    const { clientId } = this.props;
    let activationDate = null, supportDayCount = null;
    if (this.needShowPacsParamControls())
      ({ activationDate, supportDayCount } = this.state);

    const connectionCount = this.state.connectionCount;
    const webConnectionCount = this.state.webConnectionCount || null;
    const webValidityTerm = !webConnectionCount ? null : this.state.webValidityTerm;

    return this.props.createNewOfflineLicense({
      clientId, productName, licenseKeyType,
      deviceKey, activationDate, supportDayCount, connectionCount,
      webConnectionCount, webValidityTerm, dicomConnectionLimitationType
    })
      .then(unwrapResult)
      .then(license => {
        this.props.showModalMessage("License created", license.key)
      })
  };

  getUpdatePromise = () => {
    const { supportDayCount, dicomConnectionLimitationType, deviceKey } = this.state;
    const { licenseId, updateOfflineLicense } = this.props;
    const connectionCount = this.state.connectionCount;
    const webConnectionCount = this.state.webConnectionCount || null;
    const webValidityTerm = !webConnectionCount ? null : this.state.webValidityTerm;


    return updateOfflineLicense({
      licenseId, supportDayCount, webConnectionCount,
      connectionCount, webValidityTerm, dicomConnectionLimitationType, deviceKey
    });
  };

  needShowPacsParamControls = () => this.state.productName === 'pacs_server' && this.state.licenseKeyType === '00';

  onSubmit = () => {
    this.onClose();
    const submitPromise = this.getOnSubmitPromise();
    if (submitPromise === null) return;

    submitPromise
      .catch(() => {
        this.props.showModalMessage("Error", this.props.creatingUpdatingError.toString());
      });
  };

  isDeviceKeyCorrect = () => isDeviceKeyValid(this.state.deviceKey.trim());

  renderKeyCheckingComment = () => {
    let message = null;
    if (!this.isDeviceKeyCorrect()) {
      message = this.state.deviceKey.length === 0 ?
        "Enter device key" :
        "Device key is not correct";
    }
    if (message === null) return null;

    return <FormControl.Static className='text-danger'>{message}</FormControl.Static>;
  };

  getModalData = (mode) => {
    switch (mode) {
      case Mode.Create:
        return {
          label: 'Client name',
          value: this.props.clientName,
          deviceKeyElement: <DeviceKeyInputNormalized onChange={this.onDeviceKeyChanged} />,
          activationDateElement: (<DateTime
            value={this.state.activationDate}
            dateFormat={DATE_FORMAT}
            timeFormat={false}
            closeOnSelect={true}
            onChange={this.onActivationDateChange}
          />),
        };
      case Mode.Edit:
        return {
          label: 'License key',
          value: this.props.licenseKey,
          // deviceKeyElement: <FormControl disabled value={this.props.deviceKey} />,
          deviceKeyElement: <DeviceKeyInputNormalized value={this.props.deviceKey} onChange={this.onDeviceKeyChanged} />,
          activationDateElement: <FormControl disabled value={this.state.activationDate} />
        };
      default:
        return {};
    }
  };

  isWebViewerValidityTermControlDisabled = () => this.getWebConnectionCount() === 0;

  render() {
    const modalContent = this.getModalData(this.props.mode);
    const isEditMode = this.props.mode === Mode.Edit;
    return (
      <Modal show={this.props.show} onShow={this.onShow} onHide={this.onClose} backdrop="static"
        dialogClassName="offline-license-edit-dialog-modal-class">
        <Modal.Header>
          <Modal.Title>{getTitle(KeyLicenseTypeNames[this.props.temporalType])[this.props.mode]}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="noPadding">
          <OverlayLoader
            active={this.isLoading()}
            text={this.getLoaderMessage()}
          >
            <Grid fluid className="noPadding">
              <Row className="noMargin">
                <Col lg={12}>
                  <FormGroup controlId="fcModalHeader">
                    <ControlLabel>{modalContent.label}</ControlLabel>
                    <FormControl.Static>{modalContent.value}</FormControl.Static>
                  </FormGroup>
                </Col>
              </Row>
              <Row className="noMargin">
                <Col sm={3} md={3} lg={3}>
                  <FormGroup controlId="fcProdName">
                    <ControlLabel>Product name</ControlLabel>
                    <FormControl
                      componentClass="select"
                      placeholder="Product name"
                      value={this.state.productName}
                      onChange={this.onProductNameChanged}
                      disabled={isEditMode}
                    >
                      {this.getOfflineLicKeyTypes().map(
                        prodLicKeyType => (
                          <option key={prodLicKeyType.productName} value={prodLicKeyType.productName}>
                            {prodLicKeyType.productName}
                          </option>
                        )
                      )}
                    </FormControl>
                  </FormGroup>
                </Col>
                <Col sm={3} md={3} lg={3}>
                  <FormGroup controlId="fcLicKeyType">
                    <ControlLabel>License key type</ControlLabel>
                    <FormControl
                      componentClass="select"
                      placeholder="License key type"
                      value={this.state.licenseKeyType}
                      onChange={this.onLicKeyTypeChanged}
                      disabled={isEditMode}
                    >
                      {this.state.productLicKeyTypes.map(
                        type => <option key={type.code} value={type.code}>{type.name}</option>
                      )}
                    </FormControl>
                  </FormGroup>
                </Col>
                <Col sm={6} md={6} lg={6}>
                  <FormGroup controlId="fcDeviceKey">
                    <ControlLabel>Device key</ControlLabel>
                    {modalContent.deviceKeyElement}
                  </FormGroup>
                </Col>
              </Row>
              {!this.needShowPacsParamControls() ? null :
                <React.Fragment>
                  <Row className="noMargin edit-lic-margin-top">
                    <Col sm={4} md={4} lg={4}>
                      <FormGroup controlId="fcActivationDate">
                        <ControlLabel>Activation date</ControlLabel>
                        {modalContent.activationDateElement}
                      </FormGroup>
                    </Col>
                    <Col sm={8} md={8} lg={8}>
                      <FormGroup controlId="fcSupportDayCount">
                        <ControlLabel>Support day count</ControlLabel>
                        <ExpirationDateEdit
                          startDate={this.getActivationDate()}
                          term={this.getSupportDayCount()}
                          onChange={this.onSupportDayCountChanged}
                          elementType={'supportDayCount'}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="noMargin">
                    <Col sm={4} md={4} lg={4}>
                      <FormGroup controlId="fcWebConnectionCount">
                        <ControlLabel>Web Connection count</ControlLabel>
                        <FormControl
                          disabled={false}
                          value={this.getWebConnectionCount()}
                          type="number"
                          min="0"
                          onChange={this.onWebConnectionCountChange}
                        />
                      </FormGroup>
                    </Col>
                    <Col sm={8} md={8} lg={8}>
                      <FormGroup controlId="fcWebViewerValidityTerm">
                        <ControlLabel>Web viewer validity term</ControlLabel>
                        <ExpirationDateEdit
                          startDate={this.getActivationDate()}
                          term={this.getWebValidityTerm()}
                          onChange={this.onWebValidityTermChanged}
                          elementType={'webViewerValidityTerm'}
                          disabled={this.isWebViewerValidityTermControlDisabled()}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="noMargin">
                    <Col sm={4} md={4} lg={4}>
                      <FormGroup controlId="fcDICOMConnectionCount">
                        <ControlLabel>DICOM Connection count</ControlLabel>
                        <FormControl
                          disabled={false}
                          value={this.getConnectionCount()}
                          type="number"
                          min="0"
                          onChange={this.onConnectionCountChange}
                        />
                      </FormGroup>
                    </Col>
                    <Col sm={8} md={8} lg={8}>
                      <FormGroup controlId="fcDicomLimitTypes">
                        <ControlLabel>Dicom connection limitation type</ControlLabel>
                        <FormControl
                          componentClass="select"
                          value={this.getDicomConnectionLimitationType()}
                          onChange={this.onDicomConnectionLimitationTypeChange}
                        >
                          {this.getDicomConnectionLimitationTypes().map((i) => {
                            const { name, code } = i;
                            return <option key={code} value={code}>{name}</option>;
                          })}
                        </FormControl>
                      </FormGroup>
                    </Col>
                  </Row>
                </React.Fragment>
              }
              <Row className="noMargin">
                <Col lg={12} className="toRight">
                  {this.renderKeyCheckingComment()}
                </Col>
              </Row>
            </Grid>
          </OverlayLoader>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={this.onSubmit}
            disabled={this.isSubmitButtonDisabled()}
          >
            {SubmitButtonCaptions[this.props.mode]}
          </Button>
          <Button onClick={this.onClose}>Close</Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

const getDefaultSupportDayCount = () => {
  const m = moment();
  return m.clone().add('years', OfflineLicensePeriodYearsCount).diff(m, 'days');
};

const defaultDayCount = getDefaultSupportDayCount();

OfflineLicenseEditDialog.defaultProps = {
  supportDayCount: defaultDayCount,
  webValidityTerm: defaultDayCount,
  activationDate: moment().format(DATE_FORMAT),
  connectionCount: 0,
  webConnectionCount: 0,
  dicomConnectionLimitationType: 1,
};

const mapStateToProps = state => ({
  licenseKeyTypesState: objectDataSelectors.licenseKeyTypesState(state),
  dicomConnectionLimitationTypesState: objectDataSelectors.dicomConnectionLimitationTypesState(state),

  creatingUpdatingError: offlineLicensesSelectors.creatingUpdatingError(state),
});

const actionCreators = {
  fetchLicKeyTypes: () => objectDataActions.fetchObjectData({ objectName: objectDataNames.licenseKeyTypes }),
  fetchDicomConnectionLimitationTypes: () => objectDataActions.fetchObjectData({ objectName: objectDataNames.dicomConnectionLimitationTypes }),
  createNewOfflineLicense: offlineLicensesActions.create,
  updateOfflineLicense: offlineLicensesActions.update,

  showModalMessage: (title, text) => showModalMessage({ title, text }),
};

export default connect(mapStateToProps, actionCreators)(OfflineLicenseEditDialog);



// WEBPACK FOOTER //
// ./src/containers/offlineLicenseEditDialog.js